import React, { useEffect, useState } from 'react';
import { Form, Message, Modal, Dropdown } from 'semantic-ui-react';
import { OrganizationCategoryRead } from '../api/surveyStructureApi';
import { StyledButton, StyledCancelButton, StyledSaveButton } from 'common/components/button/StyledButton';
import { OrganizationCategory } from './OrganizationEdit';

interface OrganizationSurveyStructureModalProps {
  open: boolean;
  onClose: () => void;
  category?: OrganizationCategory;
  onSaveCategory: (updatedCategory: OrganizationCategoryRead) => void;
}

const visibilityOptions = [
  { key: 'public', text: 'Public', value: 'public' },
  { key: 'private', text: 'Private', value: 'private' },
];

const memberPromptOptions = [
  { key: 'ask_if_not_found', text: 'Ask if not found', value: 'ask_if_not_found' },
  { key: 'never_ask', text: 'Never ask', value: 'never_ask' },
  { key: 'always_ask', text: 'Always ask', value: 'always_ask' },
];

const OrganizationSurveyStructureModal: React.FC<OrganizationSurveyStructureModalProps> = (
  { open, onClose, category, onSaveCategory }) => {
  const isEditing = !!category;
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState('');
  const [input, setInput] = useState('');
  const [question, setQuestion] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditing && category) {
      setName(category.name);
      setVisibility(category.visibility || '');
      setInput(category.input || '');
      setQuestion(category.question || '');
    } else {
      setName('');
      setVisibility('');
      setInput('');
      setQuestion('');
    }
  }, [category, isEditing]);

  const validateForm = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

const handleSubmit = () => {
  if (!validateForm()) return;

  const updatedCategory: OrganizationCategoryRead = {
    ...category,
    name,
    visibility,
    input,
    question,
    id: category?.id || crypto.randomUUID(),
    description: category?.description ?? '',
    is_active: category?.is_active ?? true,
    priority: category?.priority ?? '',
    slug: category?.slug ?? '',
    categories: category?.categories ?? [],
  };

  onSaveCategory(updatedCategory); 
  onClose();
};


  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>{isEditing ? 'Edit Category' : 'Create Category'}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field required error={!!errors.name}>
            <label>Category Name</label>
            <Form.Input
              value={name}
              onChange={(e, { value }) => setName(value)}
            />
            {errors.name && <Message negative size="tiny">{errors.name}</Message>}
          </Form.Field>

          <Form.Field>
            <label>Visibility</label>
            <Dropdown
              placeholder="Select visibility"
              fluid
              selection
              options={visibilityOptions}
              value={visibility}
              onChange={(e, { value }) => setVisibility(value as string)}
            />
          </Form.Field>

          <Form.Field>
            <label>Survey Prompt</label>
            <Dropdown
              placeholder="Select prompt behavior"
              fluid
              selection
              options={memberPromptOptions}
              value={input}
              onChange={(e, { value }) => setInput(value as string)}
            />
          </Form.Field>

          <Form.Field>
            <label>Question</label>
            <Form.Input
              value={question}
              onChange={(e, { value }) => setQuestion(value)}
            />
          </Form.Field>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
          <StyledCancelButton onClick={onClose} />
          <StyledButton color='blue' onClick={handleSubmit} label="OK"/>
        </div>
      </Modal.Actions>
    </Modal>
  );
};

export default OrganizationSurveyStructureModal;
